import { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { userAPI, bookingAPI } from '../services/api';
import { pushDocumentScrollLock, popDocumentScrollLock } from '../utils/documentScrollLock';
import LoadingIndicator from '../components/LoadingIndicator';
import CancellationRefundTimeline from '../components/CancellationRefundTimeline.jsx';
import {
  bookingHasRecordedPayment,
  bookingPayNowDueAmount,
  userCanPayNowOnline,
  userCanRequestCancellation
} from '../utils/bookingPayment.js';
import { bookingNeedsParticipantCancellationSelection } from '../utils/bookingGroupCancellation.js';
import { resolveCustomerBookingTab } from '../utils/customerBookingTab.js';
import {
  bookingPackageStartDate,
  formatBookingPackageDateLabel
} from '../utils/bookingPackageDates.js';
import CancellationParticipantPicker from '../components/CancellationParticipantPicker.jsx';
import PayRemainingButton from '../components/PayRemainingButton.jsx';
import { openRazorpayBookingPayment } from '../utils/razorpayCheckout.js';
import { onlineTotalWithGatewayFee } from '../utils/paymentGateway.js';
import { resolveCustomerDisplayName } from '../utils/customerDisplayName.js';
import {
  bookingTicketUrl,
  resolveBookingTicketDownloadOptions
} from '../utils/bookingTicketDownloads.js';
import './Dashboard.css';

const HikingIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2v20M8 22h8M8 12l2-4 2 2 2-2 2 4"/>
    <path d="M8 22l2-10 2 2 2-2 2 10"/>
  </svg>
);
const MapIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/>
    <line x1="8" y1="2" x2="8" y2="18"/>
    <line x1="16" y1="6" x2="16" y2="22"/>
  </svg>
);
const HeartIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
  </svg>
);
const WhatsAppIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
);
const DocIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
);
const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
);
const CancelRequestIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M8 8l8 8M16 8l-8 8" />
  </svg>
);

const CANCELLATION_REASONS = [
  { value: 'Academic', label: 'Academic' },
  { value: 'Medical', label: 'Medical' },
  { value: 'Other', label: 'Other' }
];

function formatEventDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' });
}

function firstNonEmptyString(values = []) {
  for (const v of values) {
    const s = String(v || '').trim();
    if (s) return s;
  }
  return '';
}

function resolveTrekImage(trek) {
  if (!trek || typeof trek !== 'object') return '';
  const media = Array.isArray(trek.media) ? trek.media : [];
  const fromMedia = media
    .map((m) => (typeof m === 'string' ? m : firstNonEmptyString([m?.url, m?.imageUrl, m?.src, m?.path])))
    .find(Boolean);
  if (fromMedia) return fromMedia;
  return firstNonEmptyString([
    trek.imageUrl,
    trek.image_url,
    trek.coverImage,
    trek.cover_image,
    trek.thumbnailUrl,
    trek.thumbnail_url,
    trek.brochureUrl
  ]);
}

const Dashboard = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState({ stats: { totalTreks: 0, kmCovered: 0 }, recentActivity: [] });
  const [myBookings, setMyBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingTicketId, setDownloadingTicketId] = useState('');
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('Other');
  const [cancelDetails, setCancelDetails] = useState('');
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [cancelSuccess, setCancelSuccess] = useState(false);
  const [cancelAttachment, setCancelAttachment] = useState(null);
  const [cancelParticipantRegIds, setCancelParticipantRegIds] = useState([]);
  const [payingNow, setPayingNow] = useState(false);
  const cancelFileInputRef = useRef(null);

  const reloadBookings = async () => {
    try {
      const bookingsRes = await bookingAPI.getMyBookings();
      const bookingRows = bookingsRes?.data?.data || [];
      setMyBookings(Array.isArray(bookingRows) ? bookingRows : []);
    } catch {
      /* keep current list */
    }
  };

  const displayName = useMemo(
    () => resolveCustomerDisplayName(user, myBookings),
    [user, myBookings]
  );

  useEffect(() => {
    if (!cancelModalOpen) return;
    pushDocumentScrollLock();
    return () => popDocumentScrollLock();
  }, [cancelModalOpen]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [dashboardRes, bookingsRes] = await Promise.all([
          userAPI.getDashboard(),
          bookingAPI.getMyBookings()
        ]);
        if (cancelled) return;
        const dash = dashboardRes.data.data || dashboardRes.data;
        setDashboardData({ stats: dash.stats || { totalTreks: 0, kmCovered: 0 }, recentActivity: dash.recentActivity || [] });
        const bookingRows = bookingsRes?.data?.data || [];
        setMyBookings(Array.isArray(bookingRows) ? bookingRows : []);
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.message || 'Failed to load dashboard');
          setDashboardData({ stats: { totalTreks: 0, kmCovered: 0 }, recentActivity: [] });
          setMyBookings([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const upcomingBooking = useMemo(() => {
    const list = Array.isArray(myBookings) ? myBookings : [];
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const candidates = list.filter((b) => {
      if (resolveCustomerBookingTab(b) !== 'upcoming') return false;
      const startRaw = bookingPackageStartDate(b);
      if (!startRaw) return false;
      const d = new Date(startRaw);
      if (Number.isNaN(d.getTime())) return false;
      const startDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      return startDay >= today;
    });

    candidates.sort((a, b) => {
      const aHasTicket = bookingTicketUrl(a) ? 1 : 0;
      const bHasTicket = bookingTicketUrl(b) ? 1 : 0;
      if (aHasTicket !== bHasTicket) return bHasTicket - aHasTicket;
      return new Date(bookingPackageStartDate(a)) - new Date(bookingPackageStartDate(b));
    });
    return candidates[0] || null;
  }, [myBookings]);

  const upcomingBookingsSorted = useMemo(() => {
    const list = Array.isArray(myBookings) ? myBookings : [];
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const candidates = list.filter((b) => {
      if (resolveCustomerBookingTab(b) !== 'upcoming') return false;
      const startRaw = bookingPackageStartDate(b);
      if (!startRaw) return false;
      const d = new Date(startRaw);
      if (Number.isNaN(d.getTime())) return false;
      const startDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      return startDay >= today;
    });
    candidates.sort((a, b) => new Date(bookingPackageStartDate(a)) - new Date(bookingPackageStartDate(b)));
    return candidates;
  }, [myBookings]);

  const upcomingEvent = useMemo(() => {
    if (!upcomingBooking) return null;
    const batch = upcomingBooking.batch || {};
    const trek = batch.trek || {};
    return {
      id: batch.id || upcomingBooking.id,
      slug: trek.slug || batch.code || batch.id || upcomingBooking.id,
      title: trek.name || batch.code || 'Your Upcoming Trip',
      imageUrl: resolveTrekImage(trek),
      shortDescription: trek.tagline || trek.description || '',
      description: trek.description || '',
      duration: formatBookingPackageDateLabel(upcomingBooking, formatEventDate),
      whatsappLink: batch.whatsappLink || ''
    };
  }, [upcomingBooking]);

  const upcomingHasPayment = useMemo(
    () => bookingHasRecordedPayment(upcomingBooking),
    [upcomingBooking]
  );
  const upcomingPayNowDue = useMemo(
    () => bookingPayNowDueAmount(upcomingBooking),
    [upcomingBooking]
  );
  const upcomingCanPayNow = useMemo(
    () => userCanPayNowOnline(upcomingBooking),
    [upcomingBooking]
  );
  const upcomingPayNowOnline = useMemo(
    () => onlineTotalWithGatewayFee(upcomingPayNowDue),
    [upcomingPayNowDue]
  );
  const isFullNowPayment = useMemo(() => {
    const total = Number(upcomingBooking?.totalAmount ?? 0);
    const advance = Number(upcomingBooking?.advanceAmount ?? 0);
    const payableNow = advance > 0 ? Math.min(advance, total) : total;
    return payableNow >= total - 0.02;
  }, [upcomingBooking]);

  const upcomingWhatsappUrl = useMemo(() => {
    return String(upcomingBooking?.batch?.whatsappLink || upcomingEvent?.whatsappLink || '').trim();
  }, [upcomingBooking, upcomingEvent]);

  const canRequestCancellation = Boolean(
    upcomingBooking?.id && userCanRequestCancellation(upcomingBooking)
  );

  const upcomingTicketOptions = useMemo(() => {
    for (const b of upcomingBookingsSorted) {
      const options = resolveBookingTicketDownloadOptions(b);
      if (options.length) return options;
    }
    if (upcomingBooking) return resolveBookingTicketDownloadOptions(upcomingBooking);
    return [];
  }, [upcomingBookingsSorted, upcomingBooking]);

  const openCancelModal = () => {
    if (!upcomingBooking?.id || !userCanRequestCancellation(upcomingBooking)) return;
    setCancelReason('Other');
    setCancelDetails('');
    setCancelAttachment(null);
    setCancelParticipantRegIds([]);
    if (cancelFileInputRef.current) cancelFileInputRef.current.value = '';
    setCancelError(null);
    setCancelSuccess(false);
    setCancelModalOpen(true);
  };

  const closeCancelModal = () => {
    setCancelModalOpen(false);
    setCancelError(null);
    setCancelSubmitting(false);
    setCancelAttachment(null);
    setCancelParticipantRegIds([]);
    if (cancelFileInputRef.current) cancelFileInputRef.current.value = '';
  };

  const handleSubmitCancellation = async () => {
    if (!upcomingBooking?.id || cancelSubmitting) return;
    setCancelError(null);
    const trimmed = cancelDetails.trim();
    if (trimmed.length < 10) {
      setCancelError('Please enter at least 10 characters describing your request.');
      return;
    }
    const needsParticipantPick = bookingNeedsParticipantCancellationSelection(upcomingBooking);
    if (needsParticipantPick && cancelParticipantRegIds.length === 0) {
      setCancelError('Select at least one participant to cancel.');
      return;
    }
    setCancelSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('bookingId', upcomingBooking.id);
      formData.append('title', cancelReason);
      formData.append('details', trimmed);
      if (needsParticipantPick) {
        formData.append('registrationIds', JSON.stringify(cancelParticipantRegIds));
      }
      if (cancelAttachment instanceof File) {
        formData.append('attachment', cancelAttachment);
      }
      await bookingAPI.requestCancellation(formData);
      setMyBookings((prev) =>
        Array.isArray(prev)
          ? prev.map((b) =>
              String(b.id) === String(upcomingBooking.id) ? { ...b, pendingCancellationRequest: true } : b
            )
          : prev
      );
      setCancelSuccess(true);
      window.setTimeout(() => {
        closeCancelModal();
      }, 2200);
    } catch (e) {
      setCancelError(e.response?.data?.message || e.message || 'Could not submit request.');
    } finally {
      setCancelSubmitting(false);
    }
  };

  const handleDownloadTicket = async (option) => {
    if (!option?.bookingId || downloadingTicketId) return;
    setDownloadingTicketId(option.id);
    try {
      const res = await bookingAPI.downloadTicketFile(option.bookingId, {
        key: option.key || undefined,
        fileId: option.fileId || undefined
      });
      const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const disposition = String(res.headers['content-disposition'] || '');
      const nameMatch = disposition.match(/filename=\"?([^\";]+)\"?/i);
      a.href = url;
      a.download = nameMatch?.[1] || 'ticket';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      if (option.url) window.open(option.url, '_blank', 'noopener,noreferrer');
    } finally {
      setDownloadingTicketId('');
    }
  };

  const handlePayNow = async () => {
    if (!upcomingBooking?.id || payingNow || !upcomingCanPayNow) return;
    setPayingNow(true);
    try {
      await openRazorpayBookingPayment({
        booking: upcomingBooking,
        paymentFor: 'advance',
        onSuccess: reloadBookings
      });
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Payment failed. Please try again.';
      if (!String(msg).toLowerCase().includes('cancelled by user')) {
        window.alert(msg);
      }
    } finally {
      setPayingNow(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-page">
        <div className="dashboard-welcome">
          <h1 className="dashboard-greeting">Namaste, {displayName}!</h1>
          <p className="dashboard-tagline">Your Next Adventure Awaits.</p>
        </div>
        <div className="dashboard-loading">
          <LoadingIndicator label="Loading dashboard..." />
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <div className="dashboard-welcome">
        <h1 className="dashboard-greeting">Namaste, {displayName}!</h1>
        <p className="dashboard-tagline">Your Next Adventure Awaits.</p>
      </div>

      {error && <div className="dashboard-error">{error}</div>}

      <div className="dashboard-stats">
        <div className="dashboard-stat-card">
          <div className="dashboard-stat-icon hiking"><HikingIcon /></div>
          <div className="dashboard-stat-content">
            <div className="dashboard-stat-value">{dashboardData.stats.totalTreks ?? 0}</div>
            <div className="dashboard-stat-label">Total Treks</div>
          </div>
        </div>
        <div className="dashboard-stat-card">
          <div className="dashboard-stat-icon map"><MapIcon /></div>
          <div className="dashboard-stat-content">
            <div className="dashboard-stat-value">
              {typeof dashboardData.stats.kmCovered === 'number'
                ? `${dashboardData.stats.kmCovered}km`
                : (dashboardData.stats.kmCovered ?? '0')}
            </div>
            <div className="dashboard-stat-label">Km Covered</div>
          </div>
        </div>
        <div className="dashboard-stat-card highlight">
          <div className="dashboard-stat-icon heart"><HeartIcon /></div>
          <div className="dashboard-stat-content">
            <div className="dashboard-stat-label">Make a Difference</div>
            <Link to="/careers" className="dashboard-stat-cta">Join Our Team As Volunteer</Link>
          </div>
        </div>
      </div>

      {upcomingEvent && (
        <div className="dashboard-upcoming-card">
          <div className="dashboard-upcoming-image">
            <img
              src={upcomingEvent.imageUrl || 'https://images.unsplash.com/photo-1551632811-561732d1e306?w=400'}
              alt={upcomingEvent.title}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = 'https://images.unsplash.com/photo-1551632811-561732d1e306?w=400';
              }}
            />
            {upcomingEvent.duration && <span className="dashboard-upcoming-badge">{upcomingEvent.duration}</span>}
          </div>
          <div className="dashboard-upcoming-body">
            <span className="dashboard-upcoming-status">UPCOMING</span>
            {upcomingEvent.duration && <p className="dashboard-upcoming-date">{upcomingEvent.duration}</p>}
            <h2 className="dashboard-upcoming-title">{upcomingEvent.title}</h2>
            <p className="dashboard-upcoming-desc">{upcomingEvent.shortDescription || upcomingEvent.description || ''}</p>
            <div className="dashboard-upcoming-actions">
              {upcomingCanPayNow ? (
                <button
                  type="button"
                  className="btn-pay-remaining btn-pay-now"
                  onClick={handlePayNow}
                  disabled={payingNow}
                >
                  {payingNow
                    ? 'Opening payment...'
                    : `${isFullNowPayment ? 'Pay Full Payment' : 'Pay Advance'} ₹${Number(
                        upcomingPayNowOnline.payableTotal || 0
                      ).toLocaleString('en-IN')}`}
                </button>
              ) : null}
              <PayRemainingButton
                booking={upcomingBooking}
                variant="dashboard"
                onPaid={reloadBookings}
              />
              {upcomingHasPayment && upcomingWhatsappUrl ? (
                <a
                  href={upcomingWhatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-whatsapp"
                >
                  <WhatsAppIcon /> Join Batch WhatsApp Group
                </a>
              ) : null}
              {upcomingTicketOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className="btn-ticket-download"
                  onClick={() => handleDownloadTicket(option)}
                  disabled={Boolean(downloadingTicketId)}
                >
                  <DownloadIcon />{' '}
                  {downloadingTicketId === option.id ? 'Downloading...' : option.label}
                </button>
              ))}
              {canRequestCancellation ? (
                <button type="button" className="btn-cancel-request" onClick={openCancelModal}>
                  <CancelRequestIcon /> Request cancellation
                </button>
              ) : null}
              <Link to={`/events/${upcomingEvent.slug}`} className="btn-prep"><DocIcon /> View Prep Guide</Link>
            </div>
            {upcomingBooking?.pendingCancellationRequest ? (
              <p className="dashboard-upcoming-cancel-note">
                Cancellation request received. Our team will review it and contact you.
              </p>
            ) : null}
          </div>
        </div>
      )}

      {cancelModalOpen && upcomingBooking?.id ? (
        <div
          className="dashboard-cancel-modal-overlay"
          role="presentation"
          onClick={closeCancelModal}
        >
          <div
            className="dashboard-cancel-modal dashboard-cancel-modal--with-policy"
            role="dialog"
            aria-labelledby="dashboard-cancel-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button type="button" className="dashboard-cancel-modal-close modal-close-circle" onClick={closeCancelModal} aria-label="Close">
              ×
            </button>
            <h2 id="dashboard-cancel-title" className="dashboard-cancel-modal-title">
              Request cancellation
            </h2>
            <p className="dashboard-cancel-modal-trip">
              <strong>Trip:</strong> {upcomingEvent.title}
            </p>
            {cancelSuccess ? (
              <>
                <p className="dashboard-cancel-modal-success">
                  Your request has been submitted. Our team will review it and get back to you.
                </p>
                <button type="button" className="btn-cancel-submit" onClick={closeCancelModal}>
                  Close
                </button>
              </>
            ) : (
              <>
                <CancellationRefundTimeline
                  compact
                  departureDate={bookingPackageStartDate(upcomingBooking) || upcomingBooking?.batch?.startDate}
                  totalAmount={
                    upcomingBooking?.totalAmount != null
                      ? Number(upcomingBooking.totalAmount)
                      : undefined
                  }
                  refundSlabs={
                    upcomingBooking?.refundSlabs ||
                    upcomingBooking?.batch?.trek?.refundSlabs ||
                    null
                  }
                />
                {cancelError ? <p className="dashboard-cancel-modal-error">{cancelError}</p> : null}
                {bookingNeedsParticipantCancellationSelection(upcomingBooking) ? (
                  <CancellationParticipantPicker
                    booking={upcomingBooking}
                    selectedRegistrationIds={cancelParticipantRegIds}
                    onChange={setCancelParticipantRegIds}
                    idPrefix="dashboard-cancel-participant"
                  />
                ) : null}
                <label className="dashboard-cancel-label" htmlFor="cancel-reason">Reason</label>
                <select
                  id="cancel-reason"
                  className="dashboard-cancel-select"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                >
                  {CANCELLATION_REASONS.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
                <label className="dashboard-cancel-label" htmlFor="cancel-details">Description</label>
                <textarea
                  id="cancel-details"
                  className="dashboard-cancel-textarea"
                  rows={5}
                  maxLength={8000}
                  value={cancelDetails}
                  onChange={(e) => setCancelDetails(e.target.value)}
                  placeholder="Tell us why you need to cancel (policy and refunds apply as per your booking terms)."
                />
                <label className="dashboard-cancel-label" htmlFor="cancel-attachment">
                  Supporting document <span className="dashboard-cancel-optional">(optional)</span>
                </label>
                <input
                  ref={cancelFileInputRef}
                  id="cancel-attachment"
                  className="dashboard-cancel-file"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.doc,.docx,application/pdf,image/*"
                  onChange={(e) => setCancelAttachment(e.target.files?.[0] || null)}
                />
                <p className="dashboard-cancel-file-hint">
                  PDF, images, or Word — up to 15 MB. Leave empty if you do not have a document to attach.
                </p>
                {cancelAttachment ? (
                  <p className="dashboard-cancel-file-name">
                    Selected: {cancelAttachment.name}{' '}
                    <button
                      type="button"
                      className="dashboard-cancel-file-clear"
                      onClick={() => {
                        setCancelAttachment(null);
                        if (cancelFileInputRef.current) cancelFileInputRef.current.value = '';
                      }}
                    >
                      Remove
                    </button>
                  </p>
                ) : null}
                <div className="dashboard-cancel-modal-actions">
                  <button type="button" className="btn-cancel-secondary" onClick={closeCancelModal} disabled={cancelSubmitting}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn-cancel-submit"
                    onClick={handleSubmitCancellation}
                    disabled={cancelSubmitting}
                  >
                    {cancelSubmitting ? 'Submitting…' : 'Submit request'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}

      <section className="dashboard-section">
        <div className="dashboard-section-head">
          <h2 className="dashboard-section-title">Recent Activity & Achievements</h2>
          <Link to="/bookings" className="dashboard-section-link">View All History →</Link>
        </div>
        <div className="dashboard-activity-grid">
          {dashboardData.recentActivity.length === 0 ? (
            <p className="dashboard-empty">No completed activities yet. Your completed treks and certificates will appear here.</p>
          ) : (
            dashboardData.recentActivity.map((activity) => (
              <div key={activity.id} className="dashboard-activity-card">
                <div className="dashboard-activity-image">
                  <img src={activity.imageUrl || activity.image || 'https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?w=200'} alt={activity.title} />
                </div>
                <div className="dashboard-activity-info">
                  <h3>{activity.title}</h3>
                  <p className="dashboard-activity-meta">{activity.completedAt ? `Completed ${formatEventDate(activity.completedAt)}` : activity.dateLabel || ''}</p>
                  {activity.certificateUrl && (
                    <a href={activity.certificateUrl} target="_blank" rel="noopener noreferrer" className="dashboard-activity-cert"><DownloadIcon /> Download Certificate</a>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
